/* eslint-disable */
import React, { useCallback, useRef, useEffect, useState } from "react";
// MOMENT
import moment from "moment";
// ANT DESIGN COMPONENTS
import { Button, Card, Tag, Switch, notification } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateSendCommand } from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import {
  decodeFrontEndMessage,
  computeDebounceKey,
  isFaultMessage,
} from "../../../flatbuffers/decoders/frontEndMessageDecoder";
import {
  applyDeltaToState,
  createDefaultState,
  DisplayMapMode,
} from "../../../flatbuffers/decoders/applyDeltaToState";
import { createUiCmdPayload } from "../../../flatbuffers/builders/uiCmdBuilder";
import { FaultMessageT } from "../../../flatbuffers/generated/fault/fault-message";
// CUSTOM HOOKS
import useGetAllKeys from "../../../customHooks/GET_requests/useGetAllKeys";
import useGetBankProtocols from "../../../customHooks/GET_requests/useGetBankProtocols";
import {
  SentWebSocketCommandSummary,
  showWebSocketCommandSentNotification,
} from "./WebSocketCommandSentAlert";
// MONITORING STATE
import { useBankDispatch } from "../socketMessageState/BankMessagesContext";
import socketEmitter from "./MonitoringWebSocketEmitter";
// CHANGE DETECTION
import hasStateChanged from "./hasStateChanged";
// INTERFACES
import { RootState } from "../../../app/store";
import { LayoutInterface, SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { uuidToString } from "../../../utils/uuid";
import {
  compoundKey,
  bankKey,
  faultKey,
  faultKeyPrefix,
  BankKey,
  CompoundKey,
  FaultKey,
} from "../../../utils/typedKeys";

const FAULT_PERSISTENCE_KEY_PREFIX = "liftnet_expired_faults_";

// Map FaultContent to Messages API device_type for debounce lookup
const faultContentToDeviceType = (fc: FaultContent): string => {
  switch (fc) {
    case FaultContent.ElevatorFaultMessage:
      return "ELEVATOR";
    case FaultContent.EscalatorFaultMessage:
      return "ESCALATOR";
    default:
      return "";
  }
};

// LocalStorage helpers for fault persistence (stores FaultKey strings)
const getPersistedFaults = (bankIdStr: string): Set<FaultKey> => {
  const storageKey = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankIdStr}`;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return new Set(JSON.parse(stored) as FaultKey[]);
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to load persisted faults:", e);
  }
  return new Set();
};

const savePersistedFaults = (bankIdStr: string, faults: Set<FaultKey>) => {
  const storageKey = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankIdStr}`;
  try {
    if (faults.size > 0) {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(faults)));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to save persisted faults:", e);
  }
};

const normalizeOfflineWebSocketBaseUrl = (baseUrl: string) => {
  const sanitizedBaseUrl = baseUrl.trim();
  if (!sanitizedBaseUrl) return "";

  if (sanitizedBaseUrl.startsWith(":")) {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProtocol}//${window.location.hostname}${sanitizedBaseUrl}`;
  }

  return sanitizedBaseUrl.replace(/^http:/i, "ws:").replace(/^https:/i, "wss:");
};

const buildOfflineWebSocketUrl = (socketPrefix: string) => {
  const normalizedBaseUrl = normalizeOfflineWebSocketBaseUrl(socketPrefix).replace(/\/+$/, "");
  if (!normalizedBaseUrl) return "";
  if (normalizedBaseUrl.endsWith("/monitoring")) return `${normalizedBaseUrl}/`;
  return `${normalizedBaseUrl}/monitoring/`;
};

const shouldUseOfflineWebSocketPrefix =
  process.env.REACT_APP_SOCKET_DINAMIC_IP?.trim().toUpperCase() === "TRUE";

function MonitoringWebSocketConnectionOffline({
  serverId,
  serverName,
  channelId,
  layouts,
}: {
  serverId: string;
  serverName: string;
  channelId: string;
  layouts: LayoutInterface[];
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const socket = useRef<WebSocket | null>(null);
  const myIntervalCheckSocketConnection: any = useRef(null);
  const CONNECTING = 0;
  const OPEN = 1;
  const CLOSING = 2;
  const CLOSED = 3;
  const dispatch = useBankDispatch();
  const dispatchRedux = useDispatch();

  // ************************************************ */
  // FAULT TIMER STATE ****************************** */
  // eslint-disable-next-line no-undef
  const activeFaults = useRef<Map<FaultKey, ReturnType<typeof setTimeout>>>(new Map());
  const expiredFaults = useRef<Set<FaultKey>>(new Set());
  const persistedFaultsByBank = useRef<Map<string, Set<FaultKey>>>(new Map());
  const messageDebounceMap = useRef<Map<string, number>>(new Map());
  const lastMessagesByLift = useRef<Map<string, SocketMessageInterface>>(new Map());
  const liftStateRef = useRef<Map<CompoundKey, SocketMessageInterface>>(new Map());
  const pendingDispatches = useRef<Map<CompoundKey, SocketMessageInterface>>(new Map());
  const deltaDebounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const rafHandle = useRef<number | null>(null);
  const displayMapModes = useRef<Map<BankKey, DisplayMapMode>>(new Map());
  const protocolByBank = useRef<Map<string, string>>(new Map());
  const prevDebounceFingerprint = useRef<string>("");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [time, setTime] = useState(moment().format("HH:mm:ss"));
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [enabledAutoRefresh, setEnabledAutoRefresh] = useState<boolean>(true);
  const [enabledSendPing, setEnabledSendPing] = useState<boolean>(true);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const showQueueInfo = useSelector((state: RootState) => state.monitoring.showQueueInfo);
  const stopSocketConnection = useSelector(
    (state: RootState) => state.monitoring.stopSocketConnection,
  );
  const sendCommand = useSelector((state: RootState) => state.monitoring.sendCommand);

  const flushPendingDispatches = useCallback(() => {
    pendingDispatches.current.forEach((payload) => {
      dispatch({
        type: "UPDATE_MESSAGE",
        payload,
      });
    });
    pendingDispatches.current.clear();
    rafHandle.current = null;
  }, [dispatch]);

  const scheduleDispatch = useCallback(
    (stateKey: CompoundKey, payloadData: any) => {
      pendingDispatches.current.set(stateKey, payloadData);
      if (rafHandle.current === null) {
        rafHandle.current = requestAnimationFrame(flushPendingDispatches);
      }
    },
    [flushPendingDispatches],
  );

  const getDebounceTime = useCallback((faultContent: FaultContent, code: number): number => {
    const deviceType = faultContentToDeviceType(faultContent);
    return messageDebounceMap.current.get(`${deviceType}:${code}`) ?? 0;
  }, []);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const selectedOfflineWebSocketBaseUrl = shouldUseOfflineWebSocketPrefix
    ? process.env.REACT_APP_SOCKET_OFFLINE_PREFIX || ""
    : process.env.REACT_APP_WEB_SOCKET_OFFLINE || "";
  const OFFLINE_WEB_SOCKET_ABSOLUTE_URL = buildOfflineWebSocketUrl(
    selectedOfflineWebSocketBaseUrl,
  );
  const { keys } = useGetAllKeys();

  // Resolve per-bank adapter protocol type (e.g. "mce") from /banks/:id, since
  // the monitor-view layout payload does not include the full adapter object.
  const bankIds = React.useMemo(() => layouts.map((l) => l.idBank), [layouts]);
  const { protocolByBank: bankProtocols } = useGetBankProtocols(bankIds);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const shouldFaultBlink = useCallback(
    (activeFaultList: FaultMessageT[], bankIdStr: string, targetLiftId: string): boolean => {
      const persistedFaults = persistedFaultsByBank.current.get(bankIdStr) || new Set<FaultKey>();
      const prefix = faultKeyPrefix(bankIdStr, targetLiftId);
      const allExpiredFaults = Array.from(expiredFaults.current);
      const hasExpiredFaultsForThisLift = allExpiredFaults.some((key) => key.startsWith(prefix));
      const matchesPersistedFault = activeFaultList.some((f) =>
        persistedFaults.has(faultKey(bankIdStr, targetLiftId, f.faultType, f.fault?.message ?? 0)),
      );

      return hasExpiredFaultsForThisLift || matchesPersistedFault;
    },
    [],
  );

  const manageFaultTimers = useCallback(
    (
      faultsWithDebounce: Array<{
        fault: FaultMessageT;
        debounceMs: number;
      }>,
      bankIdStr: string,
      targetLiftId: string,
      isMce: boolean,
    ) => {
      const incomingFaultKeys = new Set(
        faultsWithDebounce.map((f) =>
          faultKey(bankIdStr, targetLiftId, f.fault.faultType, f.fault.fault?.message ?? 0),
        ),
      );
      const currentFaults = activeFaults.current;
      const prefix = faultKeyPrefix(bankIdStr, targetLiftId);

      const triggerImmediateUpdate = () => {
        const key = faultKeyPrefix(bankIdStr, targetLiftId);
        const lastMsg = lastMessagesByLift.current.get(key);
        if (lastMsg) {
          const shouldBlink = shouldFaultBlink(lastMsg.activeFaults, bankIdStr, targetLiftId);
          dispatch({
            type: "UPDATE_MESSAGE",
            payload: { ...lastMsg, faults: shouldBlink },
          });
        }
      };

      const updatePersistence = (fk: FaultKey, shouldAdd: boolean) => {
        try {
          const currentSet = persistedFaultsByBank.current.get(bankIdStr) || new Set<FaultKey>();
          if (shouldAdd) currentSet.add(fk);
          else currentSet.delete(fk);
          persistedFaultsByBank.current.set(bankIdStr, currentSet);
          savePersistedFaults(bankIdStr, currentSet);
        } catch (e) {
          console.error("Error updating fault persistence:", e);
        }
      };

      // 1. START TIMERS — faults with a configured debounce time blink after
      // the delay elapses. For MCE banks, faults without a debounce time
      // (<= 0) blink immediately, since a zero debounce means "no delay".
      // For non-MCE banks, faults without a debounce time are ignored.
      faultsWithDebounce.forEach(({ fault, debounceMs }) => {
        const fk = faultKey(bankIdStr, targetLiftId, fault.faultType, fault.fault?.message ?? 0);
        if (debounceMs <= 0) {
          if (isMce && !expiredFaults.current.has(fk)) {
            expiredFaults.current.add(fk);
            updatePersistence(fk, true);
          }
          return;
        }
        if (!currentFaults.has(fk) && !expiredFaults.current.has(fk)) {
          const timer = setTimeout(() => {
            expiredFaults.current.add(fk);
            currentFaults.delete(fk);
            updatePersistence(fk, true);
            triggerImmediateUpdate();
          }, debounceMs);
          currentFaults.set(fk, timer);
        }
      });

      // 2. CANCEL TIMERS
      Array.from(currentFaults.keys()).forEach((fk) => {
        if (fk.startsWith(prefix)) {
          if (!incomingFaultKeys.has(fk)) {
            const timer = currentFaults.get(fk);
            if (timer) clearTimeout(timer);
            currentFaults.delete(fk);
            updatePersistence(fk, false);
          }
        }
      });

      // 3. STOP BLINKING
      Array.from(expiredFaults.current).forEach((fk) => {
        if (fk.startsWith(prefix)) {
          if (!incomingFaultKeys.has(fk)) {
            expiredFaults.current.delete(fk);
            updatePersistence(fk, false);
            triggerImmediateUpdate();
          }
        }
      });
    },
    [dispatch, shouldFaultBlink],
  );

  const checkSocketConnection = () => {
    if (socket.current?.readyState === CLOSED && enabledAutoRefresh) {
      // eslint-disable-next-line no-use-before-define
      onConnect();
    }
  };

  const onMessageFlatBuffer = useCallback(
    async (socketMessage: ArrayBuffer | Blob | string) => {
      if (typeof socketMessage === "string") {
        console.error("❌ WS sent STRING instead of binary:", socketMessage);
        return;
      }
      let buffer: ArrayBuffer;
      if (socketMessage instanceof Blob) {
        buffer = await socketMessage.arrayBuffer();
      } else {
        buffer = socketMessage;
      }

      const bytes = new Uint8Array(buffer);
      try {
        const fem = decodeFrontEndMessage(bytes);
        if (!fem) return;

        const bankIdStr = uuidToString(fem.bankId!);
        const liftIdStr = uuidToString(fem.liftId!);
        const stateKey = compoundKey(fem.bankId!, fem.liftId!);
        const bk = bankKey(fem.bankId!);

        // Look up or create default state for this lift
        const existingState =
          liftStateRef.current.get(stateKey) || createDefaultState(fem.bankId!, fem.liftId!);

        // Apply the delta to get merged state
        const mergedState = applyDeltaToState(existingState, fem, displayMapModes.current.get(bk));

        // Run fault timer logic with the merged activeFaults
        const { activeFaults: activeFaultList } = mergedState;
        const faultsWithDebounce = activeFaultList.map((fault) => {
          const debounceMs = getDebounceTime(fault.faultType, fault.fault?.message ?? 0);
          return { fault, debounceMs };
        });
        const isMce = protocolByBank.current.get(bankIdStr) === "mce";
        manageFaultTimers(faultsWithDebounce, bankIdStr, liftIdStr, isMce);

        const shouldBlink = shouldFaultBlink(activeFaultList, bankIdStr, liftIdStr);

        const isSocketClosed = socket.current?.readyState === WebSocket.CLOSED;

        const payloadData: SocketMessageInterface = {
          ...mergedState,
          faults: shouldBlink,
          connected: mergedState.connected && !isSocketClosed,
        };

        // Save merged state for future delta accumulation
        liftStateRef.current.set(stateKey, mergedState);

        // SAVE to ref so timers can access it later
        const liftFaultPrefix = faultKeyPrefix(bankIdStr, liftIdStr);
        const prevPayload = lastMessagesByLift.current.get(liftFaultPrefix);
        lastMessagesByLift.current.set(liftFaultPrefix, payloadData);

        // Only schedule dispatch if something meaningful changed
        if (hasStateChanged(prevPayload, payloadData)) {
          try {
            const schemaFrontEndMessageJson = JSON.stringify(
              fem,
              (_key, value) => (typeof value === "bigint" ? value.toString() : value),
              2
            );
            const socketMessageLiftJson = JSON.stringify(
              payloadData,
              (_key, value) => (typeof value === "bigint" ? value.toString() : value),
              2
            );

            socketEmitter.emit("preDispatchMessage", {
              timestamp: Date.now(),
              bankId: bankIdStr,
              liftId: liftIdStr,
              payloadJson: schemaFrontEndMessageJson,
              schemaFrontEndMessageJson,
              socketMessageLiftJson,
            });
          } catch (jsonError) {
            console.error(
              "[WS][DECODED][OFFLINE] Failed to stringify front_end_message payload:",
              jsonError
            );
          }

          // Check debounce time for non-fault deltas (faults have their own timer system)
          const debounceKey = computeDebounceKey(fem);
          const isFault = isFaultMessage(fem);
          const debounceMs =
            !isFault && debounceKey ? messageDebounceMap.current.get(debounceKey) ?? 0 : 0;
          if (debounceMs > 0) {
            const timerKey = `${stateKey}:${debounceKey}`;
            const existing = deltaDebounceTimers.current.get(timerKey);
            if (existing) clearTimeout(existing);

            deltaDebounceTimers.current.set(
              timerKey,
              setTimeout(() => {
                deltaDebounceTimers.current.delete(timerKey);
                const latest = lastMessagesByLift.current.get(liftFaultPrefix);
                if (latest) {
                  scheduleDispatch(stateKey, latest);
                }
              }, debounceMs),
            );
          } else {
            scheduleDispatch(stateKey, payloadData);
          }
        }
      } catch (err) {
        console.error("❌ Decoder FAILED (data arrived but is INVALID)", err);
      }
    },
    [manageFaultTimers, shouldFaultBlink, getDebounceTime, scheduleDispatch],
  );

  // ************************************************ */
  // USE CALLBACKS ********************************** */

  const onSocketOpen = useCallback(() => {
    if (socket.current?.readyState === OPEN) {
      console.warn(`Check First connection server ${serverId}`);
    }
  }, []);

  const onSocketClose = useCallback(() => {}, []);

  const onConnect = useCallback(() => {
    if (socket.current?.readyState !== WebSocket.OPEN) {
      socket.current = new WebSocket(OFFLINE_WEB_SOCKET_ABSOLUTE_URL);
      socket.current.addEventListener("open", onSocketOpen);
      socket.current.addEventListener("close", onSocketClose);
      socket.current.addEventListener("message", (event: any) => {
        onMessageFlatBuffer(event.data);
      });
    }
  }, [onSocketOpen, onSocketClose, onMessageFlatBuffer, OFFLINE_WEB_SOCKET_ABSOLUTE_URL]);

  const onDisconnect = () => {
    if (socket.current?.readyState === OPEN) {
      console.warn(`DISCONNECT connection server ${serverId}`);
    }
    socket.current?.close();
  };

  const onStartIntervalCheckSocketConnection = () => {
    myIntervalCheckSocketConnection.current = setInterval(() => {
      checkSocketConnection();
    }, 30 * 1000);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (keys && keys.length > 0) {
      const map = new Map<string, number>();
      keys.forEach((key: any) => {
        const msg = key.message;
        if (msg && msg.enum_id !== null && msg.enum_id !== undefined && key.debounce_time) {
          map.set(`${msg.device_type}:${msg.enum_id}`, key.debounce_time);
        }
      });

      // Skip if the debounce map hasn't actually changed
      const fingerprint = JSON.stringify(Array.from(map.entries()).sort());
      if (fingerprint === prevDebounceFingerprint.current) return;
      prevDebounceFingerprint.current = fingerprint;

      messageDebounceMap.current = map;

      // Reset active timers so faults re-evaluate with correct debounce values.
      activeFaults.current.forEach((timer) => clearTimeout(timer));
      activeFaults.current.clear();

      // Clear race-condition faults (added with debounce=0 before map loaded)
      // but restore any persisted faults from localStorage so they keep blinking.
      expiredFaults.current.clear();
      persistedFaultsByBank.current.forEach((faultSet) => {
        faultSet.forEach((fk) => expiredFaults.current.add(fk));
      });

      dispatch({ type: "UPDATE_DEBOUNCE_MAP", payload: map });
    }
  }, [keys]);

  useEffect(() => {
    layouts.forEach((layout) => {
      const persisted = getPersistedFaults(layout.idBank);
      if (persisted.size > 0) {
        persistedFaultsByBank.current.set(layout.idBank, persisted);
        // Pre-populate expiredFaults so blinking starts immediately on refresh
        persisted.forEach((fk) => expiredFaults.current.add(fk));
      }
      // Cache per-bank display map mode from adapter config
      const mode = (layout as any).adapter?.socket_message_priority?.displayMapMode ?? "accumulate";
      displayMapModes.current.set(layout.idBank as BankKey, mode);
    });

    if (serverId) {
      onConnect();
      onStartIntervalCheckSocketConnection();
    }
    const interval = setInterval(() => {
      setTime(moment().tz(userTimeZone).format("HH:mm:ss z"));
    }, 1000);
    return () => {
      onDisconnect();
      clearInterval(interval);
      if (myIntervalCheckSocketConnection.current) {
        clearInterval(myIntervalCheckSocketConnection.current);
      }
      activeFaults.current.forEach((timer) => clearTimeout(timer));
      activeFaults.current.clear();
      expiredFaults.current.clear();
      liftStateRef.current.clear();
      if (rafHandle.current !== null) {
        cancelAnimationFrame(rafHandle.current);
        rafHandle.current = null;
      }
      pendingDispatches.current.clear();
    };
  }, []);

  useEffect(() => {
    if (stopSocketConnection) {
      onDisconnect();
    } else {
      onConnect();
    }
  }, [stopSocketConnection]);

  // Cache per-bank adapter protocol type (e.g. "mce") resolved from /banks/:id.
  // The monitor-view layout payload does not include the adapter object, so we
  // rely on the dedicated protocol lookup hook here.
  useEffect(() => {
    bankProtocols.forEach((protocol, idBank) => {
      protocolByBank.current.set(idBank, protocol);
    });
  }, [bankProtocols]);

  useEffect(() => {
    if (!sendCommand.sendCommand) return;

    const { command, position, active, bankId, liftId } = sendCommand.body || {};

    if (
      command === undefined ||
      position === undefined ||
      active === undefined ||
      bankId === undefined ||
      liftId === undefined
    ) {
      notification.warning({
        message: "Command not sent",
        description: "Invalid command payload.",
        placement: "topRight",
        duration: 4,
      });
      dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
      return;
    }

    const commandSummary = {
      command,
      position,
      active,
      channelId,
      bankId,
      liftId,
    } as SentWebSocketCommandSummary;

    if (socket.current?.readyState === WebSocket.OPEN) {
      const payload = createUiCmdPayload({
        message: command,
        position,
        active,
        channelId: commandSummary.channelId,
        bankId,
        liftId,
      });

      socket.current.send(payload);
      showWebSocketCommandSentNotification(commandSummary);
    } else {
      notification.warning({
        message: "Command not sent",
        description: "WebSocket is disconnected. Reconnect and try again.",
        placement: "topRight",
        duration: 4,
      });
    }

    dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
  }, [sendCommand, channelId, dispatchRedux]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {showQueueInfo && (
        <div className="generalStyles__flex">
          <div className="generalStyles__mrFix">
            <Card
              title={serverName}
              style={{ width: 550, height: 250 }}
              extra={
                <div>
                  {socket.current?.readyState !== OPEN && (
                    <Button onClick={onConnect} disabled={socket.current?.readyState === OPEN}>
                      Connect
                    </Button>
                  )}
                  {socket.current?.readyState === OPEN && (
                    <Button onClick={onDisconnect} disabled={socket.current?.readyState !== OPEN}>
                      Disconnect
                    </Button>
                  )}
                  <Switch
                    onChange={setEnabledAutoRefresh}
                    checked={enabledAutoRefresh}
                    checkedChildren="Reconnection"
                    unCheckedChildren="Reconnection"
                    style={{ marginLeft: "10px" }}
                  />
                  <Switch
                    onChange={setEnabledSendPing}
                    checked={enabledSendPing}
                    checkedChildren="Ping"
                    unCheckedChildren="Ping"
                    style={{ marginLeft: "10px" }}
                  />
                </div>
              }
            >
              <p style={{ marginBottom: "-1px" }}>
                <b
                  className={`${
                    socket.current?.readyState === OPEN
                      ? "generalStyles__info"
                      : "generalStyles__warning"
                  }`}
                >
                  Status:
                </b>{" "}
                <b style={{ color: "white" }}>
                  {socket.current?.readyState === OPEN && <Tag color="green">OPEN</Tag>}
                  {socket.current?.readyState === CONNECTING && <Tag color="cyan">CONNECTING</Tag>}
                  {socket.current?.readyState === CLOSING && <Tag color="orange">CLOSING</Tag>}
                  {socket.current?.readyState === CLOSED && <Tag color="red">CLOSED</Tag>}
                </b>
              </p>
              <p style={{ marginBottom: "-1px" }}>
                <b
                  className={`${
                    socket.current?.readyState === OPEN
                      ? "generalStyles__info"
                      : "generalStyles__warning"
                  }`}
                >
                  Socket Connection:
                </b>{" "}
                <span>OFFLINE</span>
              </p>
              <p style={{ marginBottom: "-1px" }}>
                <b
                  className={`${
                    socket.current?.readyState === OPEN
                      ? "generalStyles__info"
                      : "generalStyles__warning"
                  }`}
                >
                  Server:
                </b>
                <span>{serverId}</span>
              </p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonitoringWebSocketConnectionOffline;
